import { Decimal } from "decimal.js";
import {
  AggregateQuery,
  DatabaseObject,
  Filters,
  PaginatedResponse,
} from "./class";
import { IframeHTMLAttributes } from "@vue/runtime-dom";
import axios from "axios";

export interface FileResponse {
  file: DBFile;
  upload_url: string;
}
export class DBFile extends DatabaseObject {
  static $route = "file";
  static $fileAxios = axios.create();

  filename: string | null = null;

  uploader_id: string | null = null;
  upload_timestamp: Date | null = null;
  upload_origin: string | null = null;

  extension: string | null = null;
  file_size: Decimal = new Decimal(0);

  static async uploadFile(fileObject: File) {
    const uploadData = {
      filename: fileObject.name,
      file_size: new Decimal(fileObject.size),
      content_type: fileObject.type,
    };
    const data = await this.$axios.$post<FileResponse>(
      `${this.$route}/upload-initial`,
      uploadData
    );
    const file = this.fromAxios(data.file);

    await this.$fileAxios.put(data.upload_url, fileObject, {
      headers: {
        "Content-Type": fileObject.type,
      },
    });

    await this.$axios.get(`${this.$route}/upload-confirm/${file.id}`);
    return file;
  }

  async download() {
    const url = await DBFile.$axios.$get<string>(
      `${DBFile.$route}/download/${this.id}`
    );
    const iframe = document.getElementById(
      "downloadiframe"
    ) as unknown as IframeHTMLAttributes;
    if (!iframe) {
      throw new Error("Cannot find iframe");
    }
    iframe.src = url;
  }

  async save(): Promise<void> {
    throw new Error("Cannot save file");
  }

  static async getReport(route: string, data: any): Promise<DBFile> {
    return this.fromAxios(await DBFile.$axios.$post<DBFile>(`${route}`, data));
  }
}

export type FileLinkType =
  | "unknown"
  | "AssetManagementFile"
  | "IndividualAssetManagementFile";

export class FileLink extends DatabaseObject {
  static $route = "file-link";

  name: string | null = null;
  file_id: string | null = null;
  link_type: FileLinkType = "unknown";
  link_id: string | null = null;

  file: DBFile | null = null;

  get filename() {
    return this.file?.filename;
  }

  get uploader_id() {
    return this.file?.uploader_id;
  }

  get upload_timestamp() {
    return this.file?.upload_timestamp;
  }

  get upload_origin() {
    return this.file?.upload_origin;
  }

  get extension() {
    return this.file?.extension;
  }

  get file_size() {
    return this.file?.file_size;
  }

  static async getLinks(linkType: FileLinkType, linkId: string) {
    return this.fromAxiosList<FileLink>(
      await this.$axios.get(`${this.$route}/${linkType}?id=${linkId}`)
    );
  }

  static async create<T extends typeof DatabaseObject>(
    this: T,
    object: InstanceType<T> & FileLink,
    overrideRoute?: string
  ) {
    return await super.create.call(
      this,
      object,
      `${this.$route}/${object.link_type}`
    );
  }

  static async update<T extends typeof DatabaseObject>(
    this: T,
    object: InstanceType<T> & FileLink,
    overrideRoute?: string
  ) {
    return await super.update.call(
      this,
      object,
      `${this.$route}/${object.link_type}`
    );
  }

  static async deleteObject<T extends typeof DatabaseObject>(
    this: T,
    object: InstanceType<T> & FileLink,
    overrideRoute?: string
  ) {
    return await super.deleteObject.call(
      this,
      object,
      `${this.$route}/${object.link_type}`
    );
  }

  static async list<T extends typeof DatabaseObject>(
    this: T,
    overrideRoute?: string
  ): Promise<InstanceType<T>[]> {
    throw new Error("Cannot list file links");
  }

  static async filter<T extends typeof DatabaseObject>(
    this: T,
    filter: Filters,
    overrideRoute?: string | undefined
  ): Promise<InstanceType<T>[]> {
    throw new Error("Cannot filter file links");
  }

  static async paginated<T extends typeof DatabaseObject>(
    this: T,
    filters: Filters,
    sorts: [string, boolean][],
    page: number,
    perPage: number,
    aggregates?: AggregateQuery,
    groupBy?: string[] | undefined
  ): Promise<PaginatedResponse<InstanceType<T>>> {
    throw new Error("Cannot paginate file links");
  }
}
